import { useState } from "react";
import { useTranslation } from "react-i18next";

import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";

import { confirmDelivery } from "../../deliveryConfirmationApi";
import { DeliveryDetailsPanel } from "../DeliveryDetailsPanel";
import { responseError } from "../DeliveryCostNegotiationModal";
import { Offer } from "../OfferCard/interfaces";
import { OfferSummary } from "../OfferSummary";

interface DeliveryReviewModalProps {
  offer: Offer;
  onClose: () => void;
  onConfirmed: () => void;
  onDispute: (offer: Offer) => void;
}

export const DeliveryReviewModal = ({
  offer,
  onClose,
  onConfirmed,
  onDispute,
}: DeliveryReviewModalProps) => {
  const { t } = useTranslation();
  const [isConfirming, setIsConfirming] = useState(false);
  const [actionError, setActionError] = useState("");

  const onConfirm = async () => {
    setActionError("");
    setIsConfirming(true);
    try {
      await confirmDelivery(offer.id);
      onConfirmed();
    } catch (error) {
      setActionError(responseError(error, t("deliveryReviewModal.error")));
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto p-0 sm:max-w-2xl">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>{t("deliveryReviewModal.title")}</DialogTitle>
          <DialogDescription>
            {t("deliveryReviewModal.description", { aggregator: offer.tagged_aggregator?.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6">
          <OfferSummary offer={offer} />
        </div>

        <DeliveryDetailsPanel offer={offer} />

        {actionError && (
          <p role="alert" className="px-6 text-destructive text-sm">
            {actionError}
          </p>
        )}

        <DialogFooter className="px-6 pb-6">
          <WFPButton type="button" variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </WFPButton>
          <WFPButton
            type="button"
            variant="destructive-secondary"
            disabled={isConfirming}
            onClick={() => onDispute(offer)}
          >
            {t("deliveryReviewModal.dispute")}
          </WFPButton>
          <WFPButton type="button" disabled={isConfirming} onClick={onConfirm}>
            {isConfirming && <Spinner className="size-4" />}
            {t("deliveryReviewModal.confirm")}
          </WFPButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
